import { AntDesign } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, ScrollView, Pressable } from "react-native";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import ProgressBarComponent from "@/components/ProgressBarComponent";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputChooseList from "@/components/input/InputChooseList";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";
import { CustomerDetailsIndividualObject } from "@/utils/dto/application.request";
import {
  BridgeMostRecentOccupation,
  IndividualEmploymentStatus,
} from "@/utils/dto/extracted_enums";

type FormData = {
  employmentStatus: string;
  employmentDescription: string;
  employerName: string;
  occupation: string;
};

export default function Employment() {
  const [employmentStatus, setEmploymentStatus] = useState("");
  const [occupationDescription, setOccupationDescription] = useState("");

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const resolver = classValidatorResolver(CustomerDetailsIndividualObject, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const { isSubmitting } = formState;

  const states = useAppSelector((state: any) => state);

  const onSubmit = async (data: FormData) => {
    const type = storage.getString("user.auth.applicationType");

    const customerDetails = {
      firstName: states.firstName,
      middleName: states.middleName,
      lastName: states.lastName,
      emailAddress: states.email,
      telephoneNumber: states.phone,
      taxReferenceNumber: states.taxReferenceNumber,
      passportNumber: states.passportNumber,
      nationality: states.nationalityAcronym,
      dateOfBirth: states.birth,
      citizenship: states.citizenship,
      employmentStatus: data.employmentStatus,
      employmentDescription: data.employmentDescription,
      employerName: data.employerName,
      occupation: occupationDescription,
    };

    const filteredData: { [key: string]: string | string[] } = {};

    for (const [key, value] of Object.entries(customerDetails)) {
      if (Array.isArray(value)) {
        if (!(value.length === 1 && value[0] === "")) {
          filteredData[key] = value.filter((item) => item !== "");
        }
      }
      if (value !== null && value !== undefined && value !== "") {
        filteredData[key] = value;
      }
    }
    if (type === "INDIVIDUAL") {
      dispatch(
        setForm({
          individualCustomerData: filteredData,
          kycData: { occupation: data.occupation },
        })
      );
      router.push("/kyc");
    } else if (type === "CORPORATION") {
      dispatch(
        setForm({
          individualCustomerData: filteredData,
        })
      );
      router.push("/individual/address");
    }
  };

  return (
    <ScrollView contentContainerClassName="flex-grow bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <View className="items-center justify-center w-full h-full">
        <Logo className="m-2" />
        <View className="m-2">
          <Title text={t("onboarding/individual/employment:title")} />
        </View>
        <View className="items-center h-fit w-full lg:w-3/5 lg:h-152 lg:border lg:border-solid lg:border-gray lg:rounded-md lg:shadow">
          <Pressable
            onPress={() => {
              router.back();
            }}
            className="absolute z-[60] top-0 left-4 lg:left-6 lg:top-10"
          >
            <AntDesign name="arrowleft" size={24} color="#BDBDBD" />
          </Pressable>
          <View className="w-4/5 items-center mb-1 lg:w-full">
            <ProgressBarComponent progress={0.5} animatedValue={0.75} />
          </View>
          <View className="z-40 flex-grow justify-center w-full items-center">
            <View className="z-50">
              <InputChoose
                label={t(
                  "onboarding/individual/employment:input.employmentStatus"
                )}
                name="employmentStatus"
                data={{ IndividualEmploymentStatus }}
                control={control}
                handleChange={(value) => setEmploymentStatus(value)}
              />
            </View>
            {employmentStatus === "OTHER" && (
              <View className="z-40">
                <Input
                  label={t(
                    "onboarding/individual/employment:input.employmentDescription"
                  )}
                  control={control}
                  name="employmentDescription"
                  type="alpha"
                />
              </View>
            )}
            <Input
              label={t("onboarding/individual/employment:input.employerName")}
              control={control}
              name="employerName"
              type="alpha"
              optional
            />
            <InputChooseList
              control={control}
              name="occupation"
              label={t("onboarding/kyc:input.mostrecentoccupation")}
              data={{ BridgeMostRecentOccupation }}
              handleChange={(internal, display) => {
                const formattedDisplay = display.split(" - ")[1];
                setOccupationDescription(formattedDisplay);
              }}
            />
          </View>
          <Button
            label={t("common:button.nextstep")}
            onClick={handleSubmit(onSubmit)}
          />
        </View>
        <View className="flex-grow justify-end">
          <Copyright />
        </View>
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
